/**
 * CurrentDominicaWeatherBadge
 * ───────────────────────────
 * Small read-only chip that polls the same Open-Meteo feed the mobile
 * apps consult, classifies the response with the SAME rules
 * (src/services/weather/dominicaWeather.ts), and shows operators what
 * trigger label their weather-targeted campaigns are seeing right now.
 *
 * Why duplicate the classifier instead of importing the mobile one?
 * The mobile module pulls in React Native dependencies. Re-implementing
 * the ~30 lines of pure WMO-code classification here keeps the admin
 * bundle clean. The two implementations are intentionally identical —
 * if you change one, change the other (or extract to a shared package).
 *
 * Refresh cadence: 15 minutes, matching the mobile cache TTL. Manual
 * refresh button for operators who want a fresh read right now.
 */

import { useEffect, useState, useCallback } from 'react';
import { Loader2, RefreshCw, CloudRain, Cloud, Sun, CloudDrizzle, Zap, Thermometer, Snowflake } from 'lucide-react';

const DOMINICA_LAT = 15.3014;
const DOMINICA_LNG = -61.3886;
const REFRESH_MS = 15 * 60 * 1000;

type WeatherTrigger =
  | 'rain'
  | 'heavy_rain'
  | 'thunderstorm'
  | 'cloudy'
  | 'clear'
  | 'hot'
  | 'cold';

interface WeatherReading {
  condition: WeatherTrigger;
  weatherCode: number;
  tempC: number;
  fetchedAt: number;
}

const OPEN_METEO_URL =
  `https://api.open-meteo.com/v1/forecast` +
  `?latitude=${DOMINICA_LAT}` +
  `&longitude=${DOMINICA_LNG}` +
  `&current=weather_code,temperature_2m,is_day` +
  `&timezone=America%2FDominica`;

/** Mirror of the mobile classifier — keep in sync if you change either. */
function classify(weatherCode: number, tempC: number): WeatherTrigger {
  if (weatherCode >= 95) return 'thunderstorm';
  if (weatherCode === 65 || weatherCode === 67 || weatherCode === 82) return 'heavy_rain';
  if ((weatherCode >= 51 && weatherCode <= 67) || (weatherCode >= 80 && weatherCode <= 82)) return 'rain';
  if (weatherCode === 45 || weatherCode === 48) return 'cloudy';
  if (weatherCode >= 1 && weatherCode <= 3) return 'cloudy';
  if (tempC >= 30) return 'hot';
  if (tempC <= 22) return 'cold';
  return 'clear';
}

const ICON_FOR: Record<WeatherTrigger, React.ComponentType<{ className?: string }>> = {
  rain: CloudRain,
  heavy_rain: CloudDrizzle,
  thunderstorm: Zap,
  cloudy: Cloud,
  clear: Sun,
  hot: Thermometer,
  cold: Snowflake,
};

const TONE_FOR: Record<WeatherTrigger, { dot: string; text: string; bg: string; border: string }> = {
  rain:        { dot: 'bg-sky-400',     text: 'text-sky-200',     bg: 'bg-sky-500/10',     border: 'border-sky-500/30' },
  heavy_rain:  { dot: 'bg-blue-400',    text: 'text-blue-200',    bg: 'bg-blue-500/10',    border: 'border-blue-500/30' },
  thunderstorm:{ dot: 'bg-violet-400',  text: 'text-violet-200',  bg: 'bg-violet-500/10',  border: 'border-violet-500/30' },
  cloudy:      { dot: 'bg-slate-400',   text: 'text-slate-200',   bg: 'bg-slate-500/10',   border: 'border-slate-500/30' },
  clear:       { dot: 'bg-amber-400',   text: 'text-amber-200',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30' },
  hot:         { dot: 'bg-orange-400',  text: 'text-orange-200',  bg: 'bg-orange-500/10',  border: 'border-orange-500/30' },
  cold:        { dot: 'bg-cyan-400',    text: 'text-cyan-200',    bg: 'bg-cyan-500/10',    border: 'border-cyan-500/30' },
};

const PRETTY_LABEL: Record<WeatherTrigger, string> = {
  rain: 'Rain',
  heavy_rain: 'Heavy rain',
  thunderstorm: 'Thunderstorm',
  cloudy: 'Cloudy',
  clear: 'Clear',
  hot: 'Hot',
  cold: 'Cold',
};

export function CurrentDominicaWeatherBadge() {
  const [reading, setReading] = useState<WeatherReading | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNow = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 8000);
      const res = await fetch(OPEN_METEO_URL, { signal: ctrl.signal });
      clearTimeout(timer);
      if (!res.ok) throw new Error(`Open-Meteo ${res.status}`);
      const json = await res.json();
      const c = json?.current;
      const weatherCode = Number(c?.weather_code);
      const tempC = Number(c?.temperature_2m);
      if (!Number.isFinite(weatherCode) || !Number.isFinite(tempC)) throw new Error('Bad payload');
      setReading({
        condition: classify(weatherCode, tempC),
        weatherCode,
        tempC,
        fetchedAt: Date.now(),
      });
    } catch (err: any) {
      setError(err?.message || 'Weather feed unavailable');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchNow();
    const id = setInterval(fetchNow, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchNow]);

  if (loading && !reading) {
    return (
      <div className="inline-flex items-center gap-2 rounded-full border border-gray-700 bg-gray-800/50 px-3 py-1.5 text-xs text-gray-400">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Dominica weather…
      </div>
    );
  }

  if (error && !reading) {
    return (
      <button
        onClick={fetchNow}
        title={error}
        className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs text-red-300 hover:bg-red-500/20"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        Weather feed down · retry
      </button>
    );
  }

  if (!reading) return null;

  const tone = TONE_FOR[reading.condition];
  const Icon = ICON_FOR[reading.condition];
  const stale = Date.now() - reading.fetchedAt > REFRESH_MS;
  const ago = Math.max(0, Math.floor((Date.now() - reading.fetchedAt) / 60000));

  return (
    <button
      onClick={fetchNow}
      title={
        `Live Dominica weather feed (Open-Meteo, Roseau). ` +
        `Current trigger label: "${reading.condition}". ` +
        `Campaigns whose weather_triggers include "${reading.condition}" are eligible to fire right now. ` +
        `Last refreshed ${ago} min ago. Click to refresh.`
      }
      className={`inline-flex items-center gap-2 rounded-full border ${tone.border} ${tone.bg} px-3 py-1.5 text-xs ${tone.text} hover:brightness-110 transition`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${tone.dot} ${stale ? '' : 'animate-pulse'}`} />
      <Icon className="w-3.5 h-3.5" />
      <span className="font-medium">Dominica · {PRETTY_LABEL[reading.condition]}</span>
      <span className="text-gray-400 font-mono text-[10px]">
        {reading.tempC.toFixed(1)}°C
      </span>
      {loading && <Loader2 className="w-3 h-3 animate-spin opacity-50" />}
    </button>
  );
}
